import { useState, useEffect, useCallback } from 'react';
import { getStreak, setStreak as saveStreak } from '../utils/db';
import { getToday, isConsecutiveDay } from '../utils/timeUtils';
import { getStreakMilestone } from '../utils/xpUtils';

/**
 * Hook that manages the daily streak counter.
 * A streak increments when the user completes at least one session on a new day.
 */
export function useStreak() {
  const [streak, setStreakState] = useState({ count: 0, lastDate: null });
  const [milestone, setMilestone] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const s = await getStreak();
      setStreakState(s);
      setLoading(false);
    }
    load();
  }, []);

  /**
   * Record a session completion. Updates streak if it's a new day.
   * Returns the new streak count and any milestone reached.
   */
  const recordCompletion = useCallback(async () => {
    const today = getToday();
    const current = await getStreak();

    let newStreak;

    if (current.lastDate === today) {
      // Already recorded today — no change
      return { streak: current, milestone: null };
    }

    if (current.lastDate && isConsecutiveDay(current.lastDate, today)) {
      // Consecutive day — increment streak
      newStreak = { count: current.count + 1, lastDate: today };
    } else if (!current.lastDate) {
      // First ever session
      newStreak = { count: 1, lastDate: today };
    } else {
      // Streak broken — restart
      newStreak = { count: 1, lastDate: today };
    }

    await saveStreak(newStreak);
    setStreakState(newStreak);

    const hit = getStreakMilestone(newStreak.count);
    if (hit) setMilestone(hit);

    return { streak: newStreak, milestone: hit };
  }, []);

  const clearMilestone = useCallback(() => setMilestone(null), []);

  return {
    streak,
    milestone,
    loading,
    recordCompletion,
    clearMilestone,
  };
}
